import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "texoma_cart";

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addStay = useCallback((stay) => {
    setItems((prev) => {
      const without = prev.filter(
        (i) => !(i.type === "stay" && i.listing_id === stay.listing_id && i.check_in === stay.check_in && i.check_out === stay.check_out)
      );
      return [...without, { key: `stay-${stay.listing_id}-${stay.check_in}-${stay.check_out}`, type: "stay", ...stay }];
    });
  }, []);

  const addExperience = useCallback((exp, details) => {
    const mode = exp.pricing_mode || "per_group";
    const option = mode === "both"
      ? (details?.pricing_option === "per_group" ? "per_group" : "per_person")
      : mode;
    const unit = mode === "both" && option === "per_group" ? Number(exp.group_price) || Number(exp.price) : Number(exp.price);
    const childPrice = option === "per_person" ? Number(exp.child_price) || 0 : 0;
    const maxQty = option === "per_person" ? (Number(exp.max_guests) || 10) : 10;
    const qty = Math.max(1, Math.min(details?.people || 1, maxQty));
    const children = childPrice > 0 ? Math.max(0, Math.min(details?.children || 0, maxQty - qty)) : 0;
    const key = `exp-${exp.id}-${option}${details?.event_date ? `-${details.event_date}` : ""}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        const next = option === "per_person" ? { qty, children } : { qty: Math.min(existing.qty + 1, maxQty) };
        return prev.map((i) => (i.key === key ? { ...i, ...next } : i));
      }
      return [...prev, {
        key, type: "experience", id: exp.id, name: exp.name, price: unit, qty, children,
        child_price: childPrice, pricing_option: option, max_qty: maxQty, max_guests: Number(exp.max_guests) || 0,
        event_date: details?.event_date || "", film: details?.film || "",
      }];
    });
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty: Math.max(1, Math.min(qty, (i.max_qty || 10) - (i.children || 0))) } : i)));
  }, []);

  const updateChildren = useCallback((key, children) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, children: Math.max(0, Math.min(children, (i.max_qty || 10) - i.qty)) } : i)));
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const total = items.reduce((s, i) => s + (i.type === "stay" ? Number(i.amount) || 0 : (Number(i.price) || 0) * (i.qty || 1) + (Number(i.child_price) || 0) * (i.children || 0)), 0);
    const count = items.reduce((s, i) => s + (i.type === "stay" ? 1 : i.qty), 0);
    return { items, addStay, addExperience, updateQty, updateChildren, removeItem, clearCart, total, count };
  }, [items, addStay, addExperience, updateQty, updateChildren, removeItem, clearCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
